'use client';

import { c as _c } from "react/compiler-runtime";
import { PaymentTermPeriodSchema, type PaymentTermsFormValue } from '@repo/schema/payment-terms';
import { useFieldContext } from '../../lib/form-context.ts';
import { Button, Calendar, Field, FieldContent, FieldError, FieldLabel, Input, Popover, PopoverContent, PopoverTrigger, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Spinner } from '@repo/ui/components';
import { DateFormatter } from '@repo/ui/date-formatter';
import { Calendar as CalendarIcon } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { ComponentProps, Suspense } from 'react';
import { COMPANY_DEFAULT_PAYMENT_TERMS_TRIGGER, getEquivalentPaymentTermTrigger, getPaymentTermPeriodOptions, isCompanyDefaultPaymentTerms, normalizePaymentTermTrigger, type PaymentTermsParty, requiresPaymentTermsDaysAndPeriod, useFetchPaymentTermsOptions } from '../../forms/payment-terms/payment-terms.ts';
import { ChevronDownIcon } from '@repo/ui/icons';
const CUSTOM_DAYS_VALUE = 'custom';
type PaymentTermsInputProps = ComponentProps<typeof Input> & {
  companyDefaultLabel?: string;
  editorId?: string;
  hideDatePicker?: boolean;
  isPending?: boolean;
  /** Words the periods for the customer's invoice or the carrier's bill. */
  party: PaymentTermsParty;
  showCompanyDefaultOption?: boolean;
};
export const FormPaymentTermsInput = t0 => {
  const $ = _c(15);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      label: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const label = t1 === undefined ? "Payment terms" : t1;
  const field = useFieldContext();
  let t2;
  if ($[3] !== field.name || $[4] !== label) {
    t2 = <FieldLabel htmlFor={field.name} data-sentry-element="FieldLabel" data-sentry-source-file="form-payment-terms-input.tsx">{label}</FieldLabel>;
    $[3] = field.name;
    $[4] = label;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Button className="w-fit" disabled={true} variant="outline"><Spinner className="size-3" /><ChevronDownIcon /></Button>;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props) {
    t4 = <Suspense fallback={t3} data-sentry-element="Suspense" data-sentry-source-file="form-payment-terms-input.tsx"><Loader {...props} data-sentry-element="Loader" data-sentry-source-file="form-payment-terms-input.tsx" /></Suspense>;
    $[7] = props;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== field.state.meta.errors) {
    t5 = <FieldError className="text-sm" errors={field.state.meta.errors} data-sentry-element="FieldError" data-sentry-source-file="form-payment-terms-input.tsx" />;
    $[9] = field.state.meta.errors;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t2 || $[12] !== t4 || $[13] !== t5) {
    t6 = <Field className="gap-1" data-sentry-element="Field" data-sentry-component="FormPaymentTermsInput" data-sentry-source-file="form-payment-terms-input.tsx"><FieldContent data-sentry-element="FieldContent" data-sentry-source-file="form-payment-terms-input.tsx">{t2}{t4}{t5}</FieldContent></Field>;
    $[11] = t2;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
const Loader = t0 => {
  const $ = _c(101);
  const {
    companyDefaultLabel: t1,
    disabled: t2,
    editorId,
    hideDatePicker: t3,
    isPending: t4,
    party,
    showCompanyDefaultOption: t5
  } = t0;
  const companyDefaultLabel = t1 === undefined ? "Company Default" : t1;
  const disabled = t2 === undefined ? false : t2;
  const hideDatePicker = t3 === undefined ? false : t3;
  const isPending = t4 === undefined ? false : t4;
  const showCompanyDefaultOption = t5 === undefined ? false : t5;
  const field = useFieldContext();
  const {
    data: options
  } = useFetchPaymentTermsOptions();
  let currentBillDate;
  let showDatePicker;
  let t10;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== companyDefaultLabel || $[1] !== disabled || $[2] !== editorId || $[3] !== field || $[4] !== hideDatePicker || $[5] !== isPending || $[6] !== options.periods || $[7] !== options.preset_days || $[8] !== options.triggers || $[9] !== party || $[10] !== showCompanyDefaultOption) {
    const periodOptions = getPaymentTermPeriodOptions(options.periods, party);
    const currentTrigger = field.state.value.trigger;
    const currentDays = field.state.value.days;
    const currentPeriod = field.state.value.period;
    currentBillDate = field.state.value.bill_date;
    let t11;
    if ($[18] !== currentTrigger) {
      t11 = isCompanyDefaultPaymentTerms(currentTrigger);
      $[18] = currentTrigger;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    const isCompanyDefault = t11;
    let t12;
    if ($[20] !== currentTrigger) {
      t12 = requiresPaymentTermsDaysAndPeriod(currentTrigger);
      $[20] = currentTrigger;
      $[21] = t12;
    } else {
      t12 = $[21];
    }
    const requiresDaysAndPeriod = t12;
    let t13;
    if ($[22] !== currentDays || $[23] !== options.preset_days) {
      t13 = currentDays == null || !options.preset_days.includes(currentDays);
      $[22] = currentDays;
      $[23] = options.preset_days;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    const isCustomMode = t13;
    let T0;
    let T1;
    let getDaysSelectValue;
    let handleCustomDaysChange;
    let handleDaysChange;
    let handlePeriodChange;
    let t14;
    let t15;
    let t16;
    let t17;
    let t18;
    let t19;
    let t20;
    let t21;
    let t22;
    let t23;
    if ($[25] !== companyDefaultLabel || $[26] !== currentBillDate || $[27] !== currentDays || $[28] !== currentPeriod || $[29] !== currentTrigger || $[30] !== disabled || $[31] !== editorId || $[32] !== field || $[33] !== hideDatePicker || $[34] !== isCompanyDefault || $[35] !== isPending || $[36] !== options.preset_days || $[37] !== options.triggers || $[38] !== showCompanyDefaultOption) {
      const recognizedTriggerOptions = options.triggers.flatMap(_temp);
      const currentTriggerSupportsDates = recognizedTriggerOptions.find(trigger_0 => trigger_0.canonicalTrigger === currentTrigger)?.supports_dates ?? false;
      showDatePicker = currentTriggerSupportsDates && !hideDatePicker;
      const availableTriggerValues = recognizedTriggerOptions.map(_temp2);
      const triggerOptions = showCompanyDefaultOption ? [{
        label: companyDefaultLabel,
        requires_days_and_period: false,
        value: COMPANY_DEFAULT_PAYMENT_TERMS_TRIGGER
      }, ...recognizedTriggerOptions] : recognizedTriggerOptions;
      const triggerSelectValue = isCompanyDefault ? COMPANY_DEFAULT_PAYMENT_TERMS_TRIGGER : getEquivalentPaymentTermTrigger({
        availableTriggers: availableTriggerValues,
        trigger: currentTrigger
      });
      const handleTriggerChange = value => {
        if (showCompanyDefaultOption && value === COMPANY_DEFAULT_PAYMENT_TERMS_TRIGGER) {
          field.handleChange({
            bill_date: undefined,
            days: undefined,
            period: undefined,
            trigger: COMPANY_DEFAULT_PAYMENT_TERMS_TRIGGER
          });
          return;
        }
        const nextTrigger = recognizedTriggerOptions.find(trigger_2 => trigger_2.value === value);
        if (!nextTrigger) {
          return;
        }
        field.handleChange({
          bill_date: nextTrigger.supports_dates ? currentBillDate : undefined,
          days: nextTrigger.requires_days_and_period ? currentDays ?? 30 : undefined,
          period: nextTrigger.requires_days_and_period ? currentPeriod ?? "OFCURRENTMONTH" : undefined,
          trigger: nextTrigger.canonicalTrigger
        });
      };
      let t24;
      if ($[59] !== field) {
        t24 = value_0 => {
          field.handleChange({
            ...field.state.value,
            days: value_0 === CUSTOM_DAYS_VALUE ? undefined : Number(value_0)
          });
        };
        $[59] = field;
        $[60] = t24;
      } else {
        t24 = $[60];
      }
      handleDaysChange = t24;
      let t25;
      if ($[61] !== field) {
        t25 = value_1 => {
          const numericValue = value_1.replace(/\D/g, "");
          field.handleChange({
            ...field.state.value,
            days: numericValue ? Number(numericValue) : undefined
          });
        };
        $[61] = field;
        $[62] = t25;
      } else {
        t25 = $[62];
      }
      handleCustomDaysChange = t25;
      let t26;
      if ($[63] !== field) {
        t26 = value_2 => {
          const period = PaymentTermPeriodSchema.safeParse(value_2);
          field.handleChange({
            ...field.state.value,
            period: period.success ? period.data : undefined
          });
        };
        $[63] = field;
        $[64] = t26;
      } else {
        t26 = $[64];
      }
      handlePeriodChange = t26;
      let t27;
      if ($[65] !== currentDays || $[66] !== options.preset_days) {
        t27 = () => {
          if (currentDays == null) {
            return CUSTOM_DAYS_VALUE;
          }
          if (options.preset_days.includes(currentDays)) {
            return String(currentDays);
          }
          return CUSTOM_DAYS_VALUE;
        };
        $[65] = currentDays;
        $[66] = options.preset_days;
        $[67] = t27;
      } else {
        t27 = $[67];
      }
      getDaysSelectValue = t27;
      t6 = "flex flex-wrap items-center gap-2";
      t7 = "Loader";
      t8 = "form-payment-terms-input.tsx";
      T1 = Select;
      t18 = triggerOptions;
      t19 = handleTriggerChange;
      t20 = triggerSelectValue;
      t21 = "Select";
      t22 = "form-payment-terms-input.tsx";
      const t28 = disabled || isPending;
      let t29;
      if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
        t29 = <SelectValue placeholder="Select trigger" data-sentry-element="SelectValue" data-sentry-source-file="form-payment-terms-input.tsx" />;
        $[68] = t29;
      } else {
        t29 = $[68];
      }
      if ($[69] !== field.name || $[70] !== t28) {
        t23 = <SelectTrigger className="w-max" disabled={t28} id={field.name} data-sentry-element="SelectTrigger" data-sentry-source-file="form-payment-terms-input.tsx">{t29}</SelectTrigger>;
        $[69] = field.name;
        $[70] = t28;
        $[71] = t23;
      } else {
        t23 = $[71];
      }
      T0 = SelectContent;
      t14 = editorId;
      t15 = "SelectContent";
      t16 = "form-payment-terms-input.tsx";
      t17 = triggerOptions.map(_temp3);
      $[25] = companyDefaultLabel;
      $[26] = currentBillDate;
      $[27] = currentDays;
      $[28] = currentPeriod;
      $[29] = currentTrigger;
      $[30] = disabled;
      $[31] = editorId;
      $[32] = field;
      $[33] = hideDatePicker;
      $[34] = isCompanyDefault;
      $[35] = isPending;
      $[36] = options.preset_days;
      $[37] = options.triggers;
      $[38] = showCompanyDefaultOption;
      $[39] = T0;
      $[40] = T1;
      $[41] = getDaysSelectValue;
      $[42] = handleCustomDaysChange;
      $[43] = handleDaysChange;
      $[44] = handlePeriodChange;
      $[45] = showDatePicker;
      $[46] = t14;
      $[47] = t15;
      $[48] = t16;
      $[49] = t17;
      $[50] = t18;
      $[51] = t19;
      $[52] = t20;
      $[53] = t21;
      $[54] = t22;
      $[55] = t23;
      $[56] = t6;
      $[57] = t7;
      $[58] = t8;
    } else {
      T0 = $[39];
      T1 = $[40];
      getDaysSelectValue = $[41];
      handleCustomDaysChange = $[42];
      handleDaysChange = $[43];
      handlePeriodChange = $[44];
      showDatePicker = $[45];
      t14 = $[46];
      t15 = $[47];
      t16 = $[48];
      t17 = $[49];
      t18 = $[50];
      t19 = $[51];
      t20 = $[52];
      t21 = $[53];
      t22 = $[54];
      t23 = $[55];
      t6 = $[56];
      t7 = $[57];
      t8 = $[58];
    }
    let t24;
    if ($[72] !== T0 || $[73] !== t14 || $[74] !== t15 || $[75] !== t16 || $[76] !== t17) {
      t24 = <T0 data-inline-editor-overlay={t14} data-sentry-element={t15} data-sentry-source-file={t16}>{t17}</T0>;
      $[72] = T0;
      $[73] = t14;
      $[74] = t15;
      $[75] = t16;
      $[76] = t17;
      $[77] = t24;
    } else {
      t24 = $[77];
    }
    if ($[78] !== T1 || $[79] !== t18 || $[80] !== t19 || $[81] !== t20 || $[82] !== t21 || $[83] !== t22 || $[84] !== t23 || $[85] !== t24) {
      t9 = <T1 items={t18} onValueChange={t19} value={t20} data-sentry-element={t21} data-sentry-source-file={t22}>{t23}{t24}</T1>;
      $[78] = T1;
      $[79] = t18;
      $[80] = t19;
      $[81] = t20;
      $[82] = t21;
      $[83] = t22;
      $[84] = t23;
      $[85] = t24;
      $[86] = t9;
    } else {
      t9 = $[86];
    }
    t10 = requiresDaysAndPeriod && <><Select items={[...options.preset_days.map(_temp4), {
        label: "Custom",
        value: CUSTOM_DAYS_VALUE
      }]} onValueChange={handleDaysChange} value={getDaysSelectValue()}><SelectTrigger className="w-auto" disabled={disabled || isPending}><SelectValue placeholder="Days" /></SelectTrigger><SelectContent data-inline-editor-overlay={editorId}>{options.preset_days.map(_temp5)}<SelectItem value={CUSTOM_DAYS_VALUE}>Custom</SelectItem></SelectContent></Select><div className="flex items-center gap-2">{isCustomMode && <Input className="w-16" disabled={disabled || isPending} inputMode="numeric" onChange={event => handleCustomDaysChange(event.target.value)} placeholder="Days" value={currentDays ?? ""} />}<Select items={periodOptions} onValueChange={handlePeriodChange} value={currentPeriod ?? ""}><SelectTrigger className="w-auto" disabled={disabled || isPending}><SelectValue placeholder="Select period" /></SelectTrigger><SelectContent data-inline-editor-overlay={editorId}>{periodOptions.map(_temp6)}</SelectContent></Select></div></>;
    $[0] = companyDefaultLabel;
    $[1] = disabled;
    $[2] = editorId;
    $[3] = field;
    $[4] = hideDatePicker;
    $[5] = isPending;
    $[6] = options.periods;
    $[7] = options.preset_days;
    $[8] = options.triggers;
    $[9] = party;
    $[10] = showCompanyDefaultOption;
    $[11] = currentBillDate;
    $[12] = showDatePicker;
    $[13] = t10;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    currentBillDate = $[11];
    showDatePicker = $[12];
    t10 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
    t9 = $[17];
  }
  let t11;
  if ($[87] !== currentBillDate || $[88] !== disabled || $[89] !== editorId || $[90] !== field || $[91] !== isPending || $[92] !== showDatePicker) {
    t11 = showDatePicker && <Popover><PopoverTrigger asChild={true}><Button align="left" className={cn("justify-start text-left font-normal", !currentBillDate && "text-muted-foreground")} disabled={disabled || isPending} type="button" variant="outline"><CalendarIcon className="size-4" />{currentBillDate ? <DateFormatter date={currentBillDate} kind="date" /> : "Pick bill date"}</Button></PopoverTrigger><PopoverContent align="start" className="w-auto p-0" data-inline-editor-overlay={editorId}><Calendar mode="single" onSelect={date => {
          if (date) {
            field.handleChange({
              ...field.state.value,
              bill_date: date
            });
          }
        }} selected={currentBillDate ?? undefined} /></PopoverContent></Popover>;
    $[87] = currentBillDate;
    $[88] = disabled;
    $[89] = editorId;
    $[90] = field;
    $[91] = isPending;
    $[92] = showDatePicker;
    $[93] = t11;
  } else {
    t11 = $[93];
  }
  let t12;
  if ($[94] !== t10 || $[95] !== t11 || $[96] !== t6 || $[97] !== t7 || $[98] !== t8 || $[99] !== t9) {
    t12 = <div className={t6} data-sentry-component={t7} data-sentry-source-file={t8}>{t9}{t10}{t11}</div>;
    $[94] = t10;
    $[95] = t11;
    $[96] = t6;
    $[97] = t7;
    $[98] = t8;
    $[99] = t9;
    $[100] = t12;
  } else {
    t12 = $[100];
  }
  return t12;
};
function _temp(trigger) {
  const canonicalTrigger = normalizePaymentTermTrigger(trigger.value);
  return canonicalTrigger ? [{
    ...trigger,
    canonicalTrigger
  }] : [];
}
function _temp2(trigger_1) {
  return trigger_1.value;
}
function _temp3(trigger_3) {
  return <SelectItem key={trigger_3.value} value={trigger_3.value}>{trigger_3.label}</SelectItem>;
}
function _temp4(day) {
  return {
    label: String(day),
    value: String(day)
  };
}
function _temp5(day_0) {
  return <SelectItem key={day_0} value={String(day_0)}>{day_0}</SelectItem>;
}
function _temp6(period_0) {
  return <SelectItem key={period_0.value} value={period_0.value}>{period_0.label}</SelectItem>;
}